
import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  TableFooter
} from '@material-ui/core';
import { numberFormat } from '../utils/sales';


  interface SalesAnnualComparisonByProviderProps {
    tableData: IAnnualSalesPerProvider[];
  }

const SalesAnnualComparisonByProvider: React.FC<SalesAnnualComparisonByProviderProps> = ({ tableData = [] }) => {
  const [sortColumn, setSortColumn] = useState<string>('current_year_sales');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortedTableData, setSortedTableData] = useState<IAnnualSalesPerProvider[]>(tableData);

  const totalSales12M = tableData.reduce((total, row) => total + row.current_year_sales, 0);
  const totalSales12MLY = tableData.reduce((total, row) => total + row.last_year_sales, 0);
  const relativePercentage = tableData.reduce((total, row) => total + row.relative_change, 0);
  const SalesYoYChange = ((totalSales12M - totalSales12MLY) / totalSales12MLY);

  const handleSort = (column: string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    setSortedTableData(handleSortTableData());
  }, [sortColumn, sortDirection]);

  const handleSortTableData = () => {
    return [...sortedTableData].sort((a, b) => {
      if (sortDirection === 'asc') {
        return a[sortColumn] - b[sortColumn];
      }

      return b[sortColumn] - a[sortColumn];
    });
  };

  return (
    <div className="grid-item">
      <h4 className="px-5 py-2 text-light-gray text-center bg-light-blue">
        Providers Sales Annual Comparison
      </h4>
      <div className="max-h-440 overflow-auto">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="p-0">
                <TableSortLabel
                  className="text-14 font-bold py-0 pl-2"
                  active={sortColumn === 'provider_fullname'}
                  direction={sortDirection}
                  onClick={() => handleSort('provider_fullname')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell className="p-0">
                <TableSortLabel
                  className="text-14 font-bold py-0 pl-2"
                  active={sortColumn === 'current_year_sales'}
                  direction={sortDirection}
                  onClick={() => handleSort('current_year_sales')}
                >
                  Rolling 12M
                </TableSortLabel>
              </TableCell>
              <TableCell className="p-0">
                <TableSortLabel
                  className="text-14 font-bold py-0 pl-2"
                  active={sortColumn === 'last_year_sales'}
                  direction={sortDirection}
                  onClick={() => handleSort('last_year_sales')}
                >
                  Rolling 12M LY
                </TableSortLabel>
              </TableCell>
              <TableCell className="p-0">
                <TableSortLabel
                  className="text-14 font-bold py-0 pl-2"
                  active={sortColumn === 'relative_change'}
                  direction={sortDirection}
                  onClick={() => handleSort('relative_change')}
                >
                  Relative %
                </TableSortLabel>
              </TableCell>
              <TableCell className="p-0">
                <TableSortLabel
                  className="text-14 font-bold py-0 pl-2"
                  active={sortColumn === 'total_sales_yoY_change'}
                  direction={sortDirection}
                  onClick={() => handleSort('total_sales_yoY_change')}
                >
                  Total Sales YoY % Change
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTableData.map((row, index) => (
              <TableRow key={index}>
                <TableCell className="text-12 py-1 pl-2">
                  {row.provider_fullname}
                </TableCell>
                <TableCell className="text-12 py-1 pl-2">
                  {row.current_year_sales ?
                    numberFormat(
                      row.current_year_sales,
                      'currency',
                      'long',
                      0,
                      'standard'
                    )
                    :
                    null
                  }
                </TableCell>
                <TableCell className="text-12 py-1 pl-2">
                  {row.last_year_sales ?
                    numberFormat(
                      row.last_year_sales,
                      'currency',
                      'long',
                      0,
                      'standard'
                    )
                    :
                    null
                  }
                </TableCell>
                <TableCell className="text-12 py-1 pl-2">
                  {numberFormat(
                    row.relative_change / 100,
                    'percent',
                    'short',
                    1,
                    'standard'
                  )}
                </TableCell>
                <TableCell className="text-12 py-1 pl-2">
                  {(row.current_year_sales && row.last_year_sales) ?
                    numberFormat(
                      row.total_sales_yoY_change / 100,
                      'percent',
                      'short',
                      1,
                      'standard'
                    )
                    :
                    null
                  }
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Table>
        <TableFooter>
          <TableRow>
            <TableCell className="text-14 font-bold pl-2">Total</TableCell>
            <TableCell className="text-14 font-bold">{
              numberFormat(
                totalSales12M,
                'currency',
                'long',
                0,
                'standard'
              )
            }
            </TableCell>
            <TableCell className="text-14 font-bold">
              {numberFormat(
                totalSales12MLY,
                'currency',
                'long',
                0,
                'standard'
              )}
            </TableCell>
            <TableCell className="text-14 font-bold">
              {numberFormat(
                relativePercentage / 100,
                'percent',
                'short',
                1,
                'standard'
              )}
            </TableCell>
            <TableCell className="text-14 font-bold">
              {numberFormat(
                SalesYoYChange,
                'percent',
                'short',
                1,
                'standard'
              )}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
};

export default SalesAnnualComparisonByProvider;
